import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import startOfDay from 'date-fns/startOfDay';

import FormGroup from '../../../../../../../components/global/FormGroup';
import Alert from '../../../../../../../components/global/Alert';
import {
  getFormikTouchedErrors,
  resetFormikFields,
} from '../../../../../../../util/formik';
import Label from '../../../../../../../components/global/Label';
import BoxedCustomControl from '../../../../../../../components/global/BoxedCustomControl';
import InputGroup from '../../../../../../../components/global/InputGroup';
import { getAsset } from '../../../../../../../util/assets';
import { EVENT_DETAILS_CONTEXT } from '../utils';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    email_address: PropTypes.string,
  }),
  values: PropTypes.shape({
    name: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    anniversary_type: PropTypes.string,
    event_date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  touched: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  context: PropTypes.oneOf(Object.values(EVENT_DETAILS_CONTEXT)),
  includeCelebrated: PropTypes.bool,
};

const CustomEventDetails = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  context,
  includeCelebrated = true,
}) => {
  const dateInputRef = useRef();
  const [relationshipInfoRequired, setRelationshipInfoRequired] = useState(
    !!values.first_name || values.last_name,
  );
  const errorList = Object.values(getFormikTouchedErrors(errors, touched));
  const { date } = values;

  useEffect(() => {
    if (dateInputRef?.current) {
      dateInputRef.current.readOnly = 'readonly';
    }
  }, [dateInputRef]);

  useEffect(() => {
    if (!relationshipInfoRequired) {
      resetFormikFields(handleChange, ['first_name', 'last_name']);
    }
  }, [relationshipInfoRequired]);

  return (
    <Fragment>
      <FormGroup
        id="name"
        name="name"
        error={!!errors.name}
        label={
          context === EVENT_DETAILS_CONTEXT.SLIDESHOW
            ? 'Slideshow Name'
            : 'Event Name'
        }
        placeholder="Enter event name here..."
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.name}
      />
      {includeCelebrated && (
        <div className="row">
          <div className="col-12">
            <Label id="label-relationship" className="d-block mb-1">
              Are you celebrating someone?
            </Label>
            <div className="create-step-two-column-grid mb-2">
              <BoxedCustomControl
                label="Yes"
                type="checkbox"
                id="relationship-required"
                name="relationship_required"
                error={!!errors.relationship_required}
                onChange={() => setRelationshipInfoRequired(true)}
                checked={relationshipInfoRequired}
                boxStyle="secondary"
                boxSize="sm"
              />

              <BoxedCustomControl
                label="No"
                type="checkbox"
                id="relationship-not-required"
                name="relationship_required"
                error={!!errors.relationship_required}
                onChange={() => setRelationshipInfoRequired(false)}
                checked={!relationshipInfoRequired}
                boxStyle="secondary"
                boxSize="sm"
              />
            </div>
            {relationshipInfoRequired && (
              <>
                <div className="row">
                  <FormGroup
                    id="first_name"
                    name="first_name"
                    error={!!errors.first_name}
                    label="Name of the celebrated"
                    placeholder="First name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.first_name}
                    className="col-12 col-lg-6 mb-1"
                  />
                  <FormGroup
                    id="last_name"
                    name="last_name"
                    error={!!errors.last_name}
                    label={<>&nbsp;</>}
                    labelProps={{
                      className: 'd-none d-lg-block',
                    }}
                    placeholder="Last name"
                    className="col-12 col-lg-6"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.last_name}
                  />
                </div>
              </>
            )}
          </div>
          <div className="col-12">
            <div>
              <div className="form-group">
                <Label id="date" optional>
                  Event Date
                </Label>
                <div className="datepicker-gray-light">
                  <DatePicker
                    id="date"
                    name="date"
                    customInput={
                      <InputGroup
                        id="date"
                        name="date"
                        error={!!errors.date}
                        type="text"
                        autoComplete="off"
                        inputRef={dateInputRef}
                        addon={
                          <img
                            alt=""
                            src={getAsset('/shutterbug/icons/calendar.svg')}
                          />
                        }
                      />
                    }
                    popperClassName="freemium-event-date-picker-popper"
                    wrapperClassName="freemium-event-date-picker"
                    onChange={selectedDate => {
                      handleChange({
                        target: {
                          name: 'date',
                          value: startOfDay(selectedDate),
                        },
                      });
                    }}
                    selected={date}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {errorList.length > 0 && (
        <Alert type="danger" className="mt-1 text-normal">
          <ul className="mb-0">
            {errorList.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Fragment>
  );
};

CustomEventDetails.propTypes = propTypes;

export default CustomEventDetails;
